import { useState, useRef, useEffect } from 'react';

const ProctorCamera = ({ onCameraStatus, onCameraLost }) => {
  const videoRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const streamRef = useRef(null);
  const cameraInitializedRef = useRef(false);
  const isActiveRef = useRef(false);
  const callbacksRef = useRef({
    onCameraStatus,
    onCameraLost
  });

  useEffect(() => {
    callbacksRef.current = {
      onCameraStatus,
      onCameraLost
    };
  }, [onCameraStatus, onCameraLost]);

  useEffect(() => {
    let mounted = true;

    const handleCameraLostInternal = (reason) => {
      if (!mounted) return;
      if (!isActiveRef.current) return;
      isActiveRef.current = false;
      setIsActive(false);
      setError('Camera access lost');
      callbacksRef.current.onCameraStatus?.(false);
      callbacksRef.current.onCameraLost?.(reason);
    };

    const startCamera = async () => {
      console.log('📹 ProctorCamera: Starting camera...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 320, height: 240 },
          audio: false 
        });

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        
        console.log('✅ ProctorCamera: Camera access granted');
        streamRef.current = stream;
        cameraInitializedRef.current = true;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setIsActive(true);
          isActiveRef.current = true;
          setError(null);
          callbacksRef.current.onCameraStatus?.(true);
          console.log('✅ ProctorCamera: Camera active, calling onCameraStatus(true)');
          
          stream.getVideoTracks().forEach(track => {
            track.addEventListener('ended', () => {
              console.log('⚠️ ProctorCamera: Track ended event');
              if (cameraInitializedRef.current) {
                handleCameraLostInternal('Camera was turned off during assessment');
              }
            });
          });
        }
      } catch (error) {
        if (!mounted) return;
        console.log('❌ ProctorCamera: Camera access denied:', error.message);
        setError('Camera access required');
        setIsActive(false);
        isActiveRef.current = false;
        callbacksRef.current.onCameraStatus?.(false);
      }
    };

    startCamera();

    const monitorInterval = setInterval(() => {
      if (streamRef.current && cameraInitializedRef.current) {
        const tracks = streamRef.current.getVideoTracks();
        const isStreamActive = tracks.some(track => track.readyState === 'live');
        
        if (!isStreamActive && isActiveRef.current) {
          console.log('⚠️ ProctorCamera: Monitor detected camera loss');
          handleCameraLostInternal('Camera connection lost during assessment');
        }
      }
    }, 2000);

    return () => {
      mounted = false;
      clearInterval(monitorInterval);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      cameraInitializedRef.current = false;
      isActiveRef.current = false;
    };
  }, []);

  return (
    <div className="fixed top-2 right-2 md:top-4 md:right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border p-2 md:p-3 w-32 md:w-52">
        <div className="flex items-center justify-between mb-1 md:mb-2">
          <span className="text-[10px] md:text-xs font-medium text-gray-700">Proctoring</span>
          <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
        </div>

        <div className="relative">
          {error ? (
            <div className="w-full h-16 md:h-28 bg-red-50 border border-red-200 rounded flex items-center justify-center">
              <p className="text-[10px] md:text-xs text-red-600 text-center px-1 md:px-2">{error}</p>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-16 md:h-28 bg-gray-200 rounded object-cover"
            />
          )}
        </div>

        <div className="mt-1 md:mt-2 text-center">
          <span className={`text-[10px] md:text-xs ${isActive ? 'text-green-600' : 'text-red-600'}`}>
            {isActive ? 'Active' : 'Required'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProctorCamera;
